import React from "react";
import { Info } from "lucide-react";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { DEMAND_COLOR } from "@/lib/planModel";

const InfoButton = ({ testId }) => (
  <PopoverTrigger asChild>
    <button
      type="button"
      data-testid={testId}
      onClick={(e) => e.stopPropagation()}
      aria-label="What does this mean?"
      className="inline-flex items-center justify-center h-4 w-4 rounded-full text-zinc-200 hover:text-[#E2E30A] transition-colors align-middle no-print"
    >
      <Info className="h-3.5 w-3.5" />
    </button>
  </PopoverTrigger>
);

const content = "bg-[#141414] border-white/10 rounded-none text-white w-72 no-print";

export function FieldInfo({ title, children, testId, align = "start" }) {
  return (
    <Popover>
      <InfoButton testId={testId} />
      <PopoverContent align={align} className={content} data-testid={`${testId}-content`}>
        {title && <p className="text-xs uppercase tracking-[0.15em] text-[#E2E30A] mb-2">{title}</p>}
        <div className="text-[11px] text-zinc-200 leading-relaxed space-y-1.5">{children}</div>
      </PopoverContent>
    </Popover>
  );
}

const DEMAND_ROWS = [
  { key: "rest", label: "Rest", when: "No training", carb: "~3.0 g/kg carbs" },
  { key: "low", label: "Low", when: "Under 45 min", carb: "~3.5 g/kg carbs" },
  { key: "moderate", label: "Moderate", when: "45–89 min", carb: "~4.5 g/kg carbs" },
  { key: "high", label: "High", when: "90–149 min", carb: "~6.0 g/kg carbs" },
  { key: "very_high", label: "Very High", when: "150+ min / race", carb: "~7.5 g/kg carbs" },
];

export function DemandInfo() {
  return (
    <Popover>
      <InfoButton testId="demand-info-btn" />
      <PopoverContent align="end" className={content} data-testid="demand-info-content">
        <p className="text-xs uppercase tracking-[0.15em] text-[#E2E30A] mb-1">Daily training demand</p>
        <p className="text-[11px] text-zinc-200 leading-relaxed mb-3">
          Based on your total training time that day. It sets the day's carbohydrate target — busier days get more carbs.
        </p>
        <div className="space-y-1.5">
          {DEMAND_ROWS.map((r) => (
            <div key={r.key} className="flex items-center gap-2 text-[11px]">
              <span className="h-2 w-2 rounded-full shrink-0" style={{ background: DEMAND_COLOR[r.key] }} />
              <span className="text-white font-semibold w-20 shrink-0">{r.label}</span>
              <span className="text-zinc-200 w-24 shrink-0">{r.when}</span>
              <span className="text-zinc-200">{r.carb}</span>
            </div>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}

export function FuellingInfo() {
  return (
    <Popover>
      <InfoButton testId="fuelling-info-btn" />
      <PopoverContent align="start" className={content} data-testid="fuelling-info-content">
        <p className="text-xs uppercase tracking-[0.15em] text-[#E2E30A] mb-1">Carb fuelling around your session</p>
        <p className="text-[11px] text-zinc-200 leading-relaxed mb-3">
          How much carbohydrate to take before, during and after the session. The colour shows the fuelling need:
          <span className="text-[#34C759]"> green = light</span>,<span className="text-[#FF9500]"> amber = moderate</span>,<span className="text-[#FF3B30]"> red = high</span>.
        </p>
        <div className="space-y-1.5 text-[11px]">
          <div className="flex gap-2"><span className="text-[#34C759] font-semibold w-14 shrink-0">Pre</span><span className="text-zinc-200">Carbs to eat before you start.</span></div>
          <div className="flex gap-2"><span className="text-[#FF9500] font-semibold w-14 shrink-0">During</span><span className="text-zinc-200">Grams of carbs per hour while training.</span></div>
          <div className="flex gap-2"><span className="text-[#4C9AFF] font-semibold w-14 shrink-0">Post</span><span className="text-zinc-200">Carbs to refuel with afterwards for recovery.</span></div>
        </div>
      </PopoverContent>
    </Popover>
  );
}
